import { createFileRoute } from "@tanstack/react-router";
import CheckIcon from "lucide-react/dist/esm/icons/check";
import FileCode2Icon from "lucide-react/dist/esm/icons/file-code-2";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { StatTile } from "@/components/metric/stat-tile";
import { PageBody, PageHeader, Panel } from "@/components/shell/page-frame";
import { TabNav } from "@/components/shell/tab-nav";
import { StatusBadge } from "@/components/status/status-badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  aioSummary,
  CITATION_STATUS,
  citationChecksFixture,
  FAQ_STATE,
  type FaqDraft,
  faqDraftsFixture,
  llmsTxtStatus,
  productSchemasFixture,
  SCHEMA_HEALTH
} from "@/features/aio/mock-data";
import { formatCompact, formatPercent, formatRelative } from "@/lib/format";
import { useNow } from "@/lib/use-now";

const aioSearchSchema = z.object({
  tab: z.enum(["health", "content", "citations"]).catch("health").default("health")
});

export const Route = createFileRoute("/aio")({
  validateSearch: aioSearchSchema,
  component: AioPage
});

/**
 * AIO（AI検索最適化）。「整える → 計測する → 回収する → 観測する」の 4 段ループ（第3章）。
 * 計測は分析、回収はシナリオに接続。この画面は 整える(整備) と 観測(引用) を担う。
 */
function AioPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const reviewingCount = faqDraftsFixture.filter((faq) => faq.state !== "published").length;

  return (
    <>
      <PageHeader
        kind="analysis"
        title="AIO"
        description="AI検索に発見され、引用されるための整備と観測"
      />

      <PageBody density="dense" className="grid gap-4">
        {/* 4段ループの現在地を数値で。数値には文脈を添える（原則 11-1） */}
        <section className="grid gap-3 sm:grid-cols-3">
          <StatTile
            label="構造化データ整備率"
            value={formatPercent(aioSummary.schemaCompletionRate, 0)}
            footnote={`${productSchemasFixture.length}商品中`}
            emphasis
          />
          <StatTile
            label="AI経由セッション(30日)"
            value={formatCompact(aioSummary.aiSessions30d)}
            delta={{
              value: aioSummary.aiSessionsDelta,
              polarity: "higher-is-better",
              comparisonLabel: "前月比"
            }}
          />
          <StatTile
            label="引用率"
            value={formatPercent(aioSummary.citationRate, 0)}
            footnote={
              aioSummary.citedDropped > 0 ? `${aioSummary.citedDropped}件で引用が消失` : "消失なし"
            }
          />
        </section>

        <div>
          <TabNav
            value={search.tab}
            onChange={(tab) => navigate({ search: { tab } })}
            items={[
              { value: "health", label: "データ健全性" },
              { value: "content", label: "コンテンツ監修", count: reviewingCount },
              { value: "citations", label: "引用モニタリング" }
            ]}
          />
          <div className="pt-4">
            {search.tab === "health" ? <HealthTab /> : null}
            {search.tab === "content" ? <ContentTab /> : null}
            {search.tab === "citations" ? <CitationsTab /> : null}
          </div>
        </div>
      </PageBody>
    </>
  );
}

/** 構造化データ(3-1) と llms.txt(3-2) の整備状況。 */
function HealthTab() {
  const now = useNow();
  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
      <Panel title="商品構造化データ" count={productSchemasFixture.length} flush>
        <ul className="divide-y divide-line">
          {productSchemasFixture.map((product) => {
            const health = SCHEMA_HEALTH[product.health];
            return (
              <li key={product.id} className="flex items-center gap-3 px-4 py-2.5">
                <StatusBadge tone={health.tone}>{health.label}</StatusBadge>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-fg">{product.name}</span>
                  {product.missingFields.length > 0 ? (
                    <span className="block truncate text-2xs text-fg-subtle">
                      欠落: {product.missingFields.join(", ")}
                    </span>
                  ) : (
                    <span className="block text-2xs text-fg-subtle">JSON-LD 完備</span>
                  )}
                </span>
                {product.health !== "complete" ? (
                  <Button type="button" size="xs" variant="outline">
                    補完を依頼
                  </Button>
                ) : null}
              </li>
            );
          })}
        </ul>
      </Panel>

      <Panel title="llms.txt" description="AIクローラーへの案内板">
        <div className="flex items-center gap-2">
          <span className="grid size-8 shrink-0 place-items-center rounded-md border border-line bg-surface-sunken text-fg-muted">
            <FileCode2Icon className="size-4" aria-hidden="true" />
          </span>
          <div>
            <StatusBadge tone="success">生成済</StatusBadge>
            <p className="num mt-1 text-xs text-fg-subtle">
              {llmsTxtStatus.entries}件 · 更新 {formatRelative(llmsTxtStatus.updatedAt, now)}
            </p>
          </div>
        </div>
        {llmsTxtStatus.pendingChanges > 0 ? (
          <p className="mt-3 rounded-md border border-warning-line bg-warning-subtle px-2.5 py-1.5 text-xs text-warning-fg">
            {llmsTxtStatus.pendingChanges}件の商品変更が反映待ちです（次回の自動更新で反映）。
          </p>
        ) : null}
        <Button type="button" size="sm" variant="outline" className="mt-3 w-full">
          今すぐ再生成
        </Button>
      </Panel>
    </div>
  );
}

/** FAQ・一次情報(3-3)。AIが下書き、人が監修して公開。 */
function ContentTab() {
  const [drafts, setDrafts] = useState<FaqDraft[]>(faqDraftsFixture);
  const [editing, setEditing] = useState<string | null>(null);
  const [draftText, setDraftText] = useState("");

  const publish = (id: string, answer: string) => {
    const snapshot = drafts;
    setDrafts((current) =>
      current.map((faq) => (faq.id === id ? { ...faq, answer, state: "published" } : faq))
    );
    setEditing(null);
    toast.success("FAQを公開しました", {
      action: { label: "元に戻す", onClick: () => setDrafts(snapshot) }
    });
  };

  return (
    <Panel
      title="FAQ・一次情報"
      description="AIが顧客の声から下書き。公開前の監修は必ず人が行う"
      flush
    >
      <ul className="divide-y divide-line">
        {drafts.map((faq) => {
          const state = FAQ_STATE[faq.state];
          const isEditing = editing === faq.id;
          return (
            <li key={faq.id} className="px-4 py-3">
              <div className="flex items-start gap-2">
                <StatusBadge tone={state.tone} glyph={false} className="mt-0.5">
                  {state.label}
                </StatusBadge>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-fg">{faq.question}</p>
                  {isEditing ? (
                    <Textarea
                      value={draftText}
                      onChange={(event) => setDraftText(event.target.value)}
                      rows={3}
                      className="mt-2 resize-none"
                    />
                  ) : (
                    <p className="mt-1 text-sm text-fg-muted">{faq.answer}</p>
                  )}
                  <p className="mt-1 text-2xs text-fg-subtle">出所: {faq.source}</p>
                </div>
              </div>

              {faq.state !== "published" ? (
                <div className="mt-2 flex items-center justify-end gap-2">
                  {isEditing ? (
                    <>
                      <Button
                        type="button"
                        size="xs"
                        variant="ghost"
                        onClick={() => setEditing(null)}
                      >
                        キャンセル
                      </Button>
                      <Button type="button" size="xs" onClick={() => publish(faq.id, draftText)}>
                        <CheckIcon aria-hidden="true" />
                        監修して公開
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        type="button"
                        size="xs"
                        variant="outline"
                        onClick={() => {
                          setEditing(faq.id);
                          setDraftText(faq.answer);
                        }}
                      >
                        編集
                      </Button>
                      <Button type="button" size="xs" onClick={() => publish(faq.id, faq.answer)}>
                        <CheckIcon aria-hidden="true" />
                        そのまま公開
                      </Button>
                    </>
                  )}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </Panel>
  );
}

/** 引用モニタリング(3-6)。週次定点で自社/競合の言及を観測。 */
function CitationsTab() {
  return (
    <Panel
      title="引用モニタリング"
      description="週次で主要AIに質問し、自社と競合の言及を観測"
      flush
    >
      <ul className="divide-y divide-line">
        {citationChecksFixture.map((check) => {
          const status = CITATION_STATUS[check.status];
          return (
            <li key={check.id} className="px-4 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm text-fg">「{check.query}」</p>
                  {check.competitors.length > 0 ? (
                    <p className="mt-0.5 text-2xs text-fg-subtle">
                      引用されている競合: {check.competitors.join("、")}
                    </p>
                  ) : (
                    <p className="mt-0.5 text-2xs text-fg-subtle">競合の引用なし</p>
                  )}
                </div>
                <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
              </div>
              {check.suggestedTask ? (
                <div className="mt-2 flex items-center gap-2 rounded-md border border-info-line bg-info-subtle px-2.5 py-1.5">
                  <span className="min-w-0 flex-1 text-xs text-fg">
                    改善タスク: {check.suggestedTask}
                  </span>
                  <Button type="button" size="xs" variant="outline">
                    起票
                  </Button>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </Panel>
  );
}
